import {
  Box,
  Flex,
  HStack,
  VStack,
  Divider,
  useBreakpointValue,
  Skeleton,
  Tooltip,
  Table,
  Th,
  Thead,
  Tr,
  Tbody,
  Td,
  Input,
  Button,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useState } from 'react';

import {
  utils as commonUtils,
  date as commonDate,
} from '@finley-technologies/common';

import { LoanPricingOption } from './LoanPricingOption';
import {
  AccruedInterestAndFeesSummaryGQLType,
  ResolvedLoanFragmentGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  formatCurrencyAmount,
  numberToPercentageLabel,
} from 'packages/common-frontend/utils';
import { Bank } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';
import {
  useUpdateLoanExternalLoanIdMutation,
  GetResolvedLoanByIdDocumentGQLType,
} from '~/packages/loans/requests/loans.gql.generated';

const YtdTooltipContent = ({
  data,
  title,
}: {
  data: AccruedInterestAndFeesSummaryGQLType;
  title: string;
}) => {
  return (
    <VStack gap="0.5rem" padding="1rem">
      <HStack key="total" justify="space-between" w="100%" gap="6rem">
        <span>Total</span>
        <span>
          {typeof data.totalAmount === 'number'
            ? formatCurrencyAmount(data.totalAmount, Bank.Currency.USD)
            : '-'}
        </span>
      </HStack>

      {data.annualAmounts.length > 0 &&
        data.annualAmounts.map((annualAmount, idx) => (
          <HStack
            gap="6rem"
            justify="space-between"
            key={annualAmount?.year}
            w="100%"
          >
            <span>
              {idx === 0 ? `YTD ${title}` : `${annualAmount?.year} ${title}`}
            </span>
            <span>
              {typeof annualAmount?.amount === 'number'
                ? formatCurrencyAmount(annualAmount.amount, Bank.Currency.USD)
                : '-'}
            </span>
          </HStack>
        ))}
    </VStack>
  );
};

const InterestAndFeesSection = ({
  loan,
}: {
  loan: ResolvedLoanFragmentGQLType | undefined;
}) => {
  return (
    <VStack
      sx={{
        border: '1px solid',
        borderColor: 'gray.200',
        borderRadius: 'md',
        flex: 1,
        gap: '.5rem',
        minW: '15rem',
        p: '1rem',
      }}
    >
      <Tooltip
        label={
          typeof loan?.fees.totalAccruedInterestAndFees.totalAmount ===
            'number' && (
            <YtdTooltipContent
              data={loan.fees.totalAccruedInterestAndFees}
              title="Total"
            />
          )
        }
        placement="top-start"
        sx={{
          borderRadius: 'md',
          p: '0',
        }}
      >
        <HStack justify="space-between" w="100%">
          <Typography fontWeight="medium">YTD Total</Typography>
          <Typography fontWeight="medium">
            {loan?.fees.totalAccruedInterestAndFees.annualAmounts[0]
              ? formatCurrencyAmount(
                  loan.fees.totalAccruedInterestAndFees.annualAmounts[0].amount,
                  Bank.Currency.USD,
                )
              : '-'}
          </Typography>
        </HStack>
      </Tooltip>
      <Divider />
      <Tooltip
        label={
          loan?.fees.accruedInterest && (
            <YtdTooltipContent
              data={loan.fees.accruedInterest}
              title="Interest"
            />
          )
        }
        placement="bottom-start"
        sx={{
          borderRadius: 'md',
          p: '0',
        }}
      >
        <HStack justify="space-between" w="100%">
          <Flex maxW="50%">
            <Typography variant="base-muted">YTD Interest</Typography>
          </Flex>
          <Flex textAlign="right">
            <Typography variant="base-muted">
              {loan?.fees.accruedInterest.annualAmounts[0]
                ? formatCurrencyAmount(
                    loan?.fees.accruedInterest.annualAmounts[0].amount,
                    Bank.Currency.USD,
                  )
                : '-'}
            </Typography>
          </Flex>
        </HStack>
      </Tooltip>
      <Tooltip
        label={
          loan?.fees.accruedServiceFees && (
            <YtdTooltipContent
              data={loan.fees.accruedServiceFees}
              title="Service Fees"
            />
          )
        }
        placement="bottom-start"
        sx={{
          borderRadius: 'md',
          p: '0',
        }}
      >
        <HStack justify="space-between" w="100%">
          <Flex maxW="50%">
            <Typography variant="base-muted">YTD Service Fees</Typography>
          </Flex>
          <Flex textAlign="right">
            <Typography variant="base-muted">
              {loan?.fees.accruedServiceFees.annualAmounts[0]
                ? formatCurrencyAmount(
                    loan?.fees.accruedServiceFees.annualAmounts[0].amount,
                    Bank.Currency.USD,
                  )
                : '-'}
            </Typography>
          </Flex>
        </HStack>
      </Tooltip>
    </VStack>
  );
};

const TABLE_CELL_STYLES = {
  color: colors.gray[500],
  fontWeight: 400,
  minWidth: '5rem',
  whiteSpace: 'normal',
  textAlign: 'left',
  p: '0.5rem',
};

const TABLE_HEADER_STYLES = {
  color: colors.black[95],
  fontSize: '14px',
  fontWeight: 500,
  textAlign: 'left',
  p: '0.5rem',
};

const InterestRateTable = ({
  loan,
}: {
  loan?: ResolvedLoanFragmentGQLType;
}) => {
  if (!loan) return null;

  const currentDate = commonDate.currentDateStr(); // time zone issues maybe, whatever

  const oneYearAgo = commonDate.addMonths(currentDate, -12);
  const twoMonthsAhead = commonDate.addMonths(currentDate, 2);

  const showAllRates = loan.pricing.history.length <= 12;

  const interestRates = loan.pricing.history.filter(
    (rate) =>
      showAllRates ||
      (rate.effectiveDate >= oneYearAgo &&
        rate.effectiveDate <= twoMonthsAhead),
  );

  return (
    <Table
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      flex={1}
      gap=".5rem"
      minW="15rem"
    >
      <Thead>
        <Tr>
          <Th sx={TABLE_HEADER_STYLES}>Effective Date</Th>
          <Th sx={TABLE_HEADER_STYLES}>Benchmark</Th>
          <Th sx={TABLE_HEADER_STYLES}>All-in-Rate</Th>
          <Th sx={TABLE_HEADER_STYLES}>Benchmark Determination Date</Th>
          <Th sx={TABLE_HEADER_STYLES}>Benchmark Rate</Th>
          <Th sx={TABLE_HEADER_STYLES}>Adjusted Base Rate</Th>
          <Th sx={TABLE_HEADER_STYLES}>WA Applicable Margin</Th>
        </Tr>
      </Thead>
      <Tbody>
        {interestRates.map((interestRate) => {
          const isFixedRate = interestRate.baseRate.name
            .toLowerCase()
            .includes('fixed');
          const showFallbackDate =
            interestRate.baseRate.publishedDate !==
            interestRate.baseRate.determinationDate;
          return (
            <Tr key={interestRate.effectiveDate}>
              <Td sx={TABLE_CELL_STYLES}>
                {commonDate.fullDateStrToHumanDateString(
                  interestRate.effectiveDate,
                )}
              </Td>
              <Td sx={TABLE_CELL_STYLES}>{interestRate.baseRate.name}</Td>
              <Td sx={TABLE_CELL_STYLES}>
                {interestRate.baseRate.determinationDate > currentDate
                  ? '-'
                  : numberToPercentageLabel(
                      interestRate.baseRate.adjustedRate +
                        interestRate.weightedAverageMarginRate,
                      5,
                    )}
              </Td>
              <Td sx={TABLE_CELL_STYLES}>
                <Flex alignItems="center">
                  <Typography variant="base-muted" TextProps={{ mr: '0.2rem' }}>
                    {isFixedRate
                      ? '-'
                      : commonDate.fullDateStrToHumanDateString(
                          interestRate.baseRate.determinationDate,
                        )}
                  </Typography>

                  {showFallbackDate &&
                    interestRate.baseRate.determinationDate <= currentDate && (
                      <Tooltip
                        label={`Published ${commonDate.fullDateStrToHumanDateString(
                          interestRate.baseRate.publishedDate,
                        )}`}
                        placement="top"
                        sx={{ borderRadius: 'md' }}
                      >
                        <Box display="inline-flex" alignItems="center">
                          <Icon icon="circle-info" />
                        </Box>
                      </Tooltip>
                    )}
                </Flex>
              </Td>
              <Td sx={TABLE_CELL_STYLES}>
                {interestRate.baseRate.determinationDate > currentDate
                  ? '-'
                  : numberToPercentageLabel(
                      interestRate.baseRate.publishedRate,
                      5,
                    )}
              </Td>
              <Td sx={TABLE_CELL_STYLES}>
                {interestRate.baseRate.determinationDate > currentDate
                  ? '-'
                  : numberToPercentageLabel(
                      interestRate.baseRate.adjustedRate,
                      5,
                    )}
              </Td>
              <Td sx={TABLE_CELL_STYLES}>
                {numberToPercentageLabel(
                  interestRate.weightedAverageMarginRate,
                  5,
                )}
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
};

interface LoanDetailsProps {
  loan: ResolvedLoanFragmentGQLType | undefined;
  loading: boolean;
  showDates?: boolean;
  showLoanNamingInfo?: boolean;
  hideExternalLoanId?: boolean;
}
export const LoanDetails = ({
  loan,
  loading,
  showDates = true,
  showLoanNamingInfo = false,
  hideExternalLoanId = false,
}: LoanDetailsProps) => {
  const toast = useToast();

  const memoTextAlign = useBreakpointValue(
    { base: 'left', md: 'right' },
    { ssr: false },
  );
  const memoJustifyContent = useBreakpointValue(
    { base: 'flex-start', md: 'flex-end' },
    { ssr: false },
  );

  const isEditingExternalLoanId = useDisclosure();
  const [editExternalLoanId, setEditExternalLoanId] = useState<string>();

  const [
    updateLoanExternalLoanId,
    { loading: isUpdatingExternalLoanId },
  ] = useUpdateLoanExternalLoanIdMutation({
    refetchQueries: [GetResolvedLoanByIdDocumentGQLType],
  });

  return (
    <VStack direction="column" gap="1rem" align="start" w="100%">
      <Typography variant="section-title" trim>
        Loan Details
      </Typography>
      <VStack
        sx={{
          w: '100%',
          borderWidth: '1px',
          borderRadius: 'md',
          p: '2rem',
          gap: '1rem',
        }}
      >
        {showLoanNamingInfo && (
          <>
            {loan?.name && (
              <HStack
                sx={{
                  justifyContent: 'space-between',
                  wrap: 'wrap',
                  w: '100%',
                }}
              >
                <Typography fontWeight="medium">Loan Name</Typography>
                {loading ? (
                  <Skeleton w="10rem" h="1rem" />
                ) : (
                  <Typography variant="base-muted">
                    {loan?.name ? loan.name : '-'}
                  </Typography>
                )}
              </HStack>
            )}
            <HStack
              sx={{
                justifyContent: 'space-between',
                wrap: 'wrap',
                w: '100%',
              }}
            >
              <Typography fontWeight="medium">Loan ID</Typography>
              {loading ? (
                <Skeleton w="10rem" h="1rem" />
              ) : (
                <Flex flexWrap="wrap" maxW="55%" textAlign="end">
                  <Typography variant="base-muted">
                    {loan?.id}
                    {loan?.externalLoanId && ` (${loan.externalLoanId})`}
                  </Typography>
                </Flex>
              )}
            </HStack>
          </>
        )}

        {/* *** External Loan ID *** */}
        {!hideExternalLoanId && (
          <Flex justifyContent="space-between" w="100%" alignItems="center">
            <Typography fontWeight="medium">External Loan ID</Typography>

            {loading ? (
              <Skeleton w="10rem" h="1rem" />
            ) : (
              <Flex alignItems="center" gap="0.5rem">
                {isEditingExternalLoanId.isOpen ? (
                  <>
                    <Input
                      value={editExternalLoanId}
                      textAlign="right"
                      onChange={(evt) =>
                        setEditExternalLoanId(evt.target.value)
                      }
                    />
                    <Button
                      variant="secondary"
                      colorScheme="v2-aurora"
                      w="6rem"
                      isLoading={isUpdatingExternalLoanId}
                      onClick={async () => {
                        if (!loan) return;

                        try {
                          await updateLoanExternalLoanId({
                            variables: {
                              loanId: loan.id,
                              newExternalLoanId: editExternalLoanId,
                            },
                          });

                          toast({
                            title: 'Updated External Loan ID',
                            status: 'success',
                            duration: 3000,
                            position: 'top',
                            isClosable: true,
                          });
                        } catch (error) {
                          toast({
                            title: getErrorMessageFromApolloError({
                              error,
                              defaultMessage:
                                'Failed to update external loan ID',
                            }),
                            status: 'error',
                            duration: 6000,
                            position: 'top',
                            isClosable: true,
                          });
                        } finally {
                          isEditingExternalLoanId.onClose();
                        }
                      }}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography fontWeight="medium">
                      {loan?.externalLoanId ?? '-'}
                    </Typography>
                    <IconButtonV2
                      variant="v2-ghost"
                      colorScheme="gray"
                      aria-label="Edit"
                      icon={<Icon icon="edit" />}
                      onClick={() => {
                        setEditExternalLoanId(loan?.externalLoanId ?? '-');
                        isEditingExternalLoanId.onOpen();
                      }}
                      label="Edit External Loan ID"
                      TooltipProps={{ placement: 'top' }}
                    />
                  </>
                )}
              </Flex>
            )}
          </Flex>
        )}

        <HStack
          sx={{
            justifyContent: 'space-between',
            wrap: 'wrap',
            w: '100%',
          }}
        >
          <Typography fontWeight="medium">Global Principal</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography fontWeight="medium">
              {typeof loan?.funding.totalOutstandingBalance === 'number'
                ? formatCurrencyAmount(
                    loan.funding.totalOutstandingBalance,
                    Bank.Currency.USD,
                  )
                : '-'}
            </Typography>
          )}
        </HStack>

        <LoanPricingOption loan={loan} loading={loading} />

        <HStack
          sx={{
            fontWeight: 500,
            gap: '1rem',
            justifyContent: 'space-between',
            w: '100%',
            wrap: 'wrap',
          }}
        >
          <Typography fontWeight="medium">Interest Accrual</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography fontWeight="medium">
              {loan
                ? `${loan.pricing.accrual.basis}, ${commonUtils.toTitleCase(
                    loan.pricing.accrual.cadence.type,
                  )} Accrual`
                : '-'}
            </Typography>
          )}
        </HStack>

        <HStack
          sx={{
            fontWeight: 500,
            gap: '1rem',
            justifyContent: 'space-between',
            w: '100%',
            wrap: 'wrap',
          }}
        >
          <Flex fontWeight={500} w="20rem" alignSelf="flex-start">
            <Typography fontWeight="medium">Interest Rate Schedule</Typography>
          </Flex>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <InterestRateTable loan={loan} />
          )}
        </HStack>

        <HStack
          alignItems="flex-start"
          sx={{
            gap: '1rem',
            justifyContent: 'space-between',
            py: '.25rem',
            w: '100%',
            wrap: 'wrap',
          }}
        >
          <Flex fontWeight={500} w="20rem">
            Interest and Fees
          </Flex>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <InterestAndFeesSection loan={loan} />
          )}
        </HStack>
        {showDates && (
          <>
            <HStack
              sx={{
                justifyContent: 'space-between',
                w: '100%',
                wrap: 'wrap',
              }}
            >
              <Typography fontWeight="medium">Funding Date</Typography>
              {loading ? (
                <Skeleton w="10rem" h="1rem" />
              ) : (
                <Typography variant="base-muted">
                  {typeof loan?.dates.originationDate === 'string'
                    ? commonDate.fullDateStrToHumanDateString(
                        loan.dates.originationDate,
                      )
                    : '-'}
                </Typography>
              )}
            </HStack>
            <HStack
              sx={{
                justifyContent: 'space-between',
                wrap: 'wrap',
                w: '100%',
              }}
            >
              <Typography fontWeight="medium">Maturity Date</Typography>
              {loading ? (
                <Skeleton w="10rem" h="1rem" />
              ) : (
                <Typography variant="base-muted">
                  {typeof loan?.dates.maturityDate === 'string'
                    ? commonDate.fullDateStrToHumanDateString(
                        loan.dates.maturityDate,
                      )
                    : '-'}
                </Typography>
              )}
            </HStack>
          </>
        )}

        <HStack
          sx={{
            justifyContent: 'space-between',
            gap: '1rem',
            wrap: 'wrap',
            alignItems: 'start',
            w: '100%',
          }}
        >
          <Box minW="20rem" fontWeight={500}>
            Memo
          </Box>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Flex
              sx={{
                flex: 1,
                gap: '.5rem',
                color: colors.gray[600],
                justifyContent: memoJustifyContent,
                textAlign: memoTextAlign,
                minW: '10rem',
              }}
            >
              {loan?.memo ?? '-'}
            </Flex>
          )}
        </HStack>

        {/* *** Escrow Amount *** */}
        {loan?.customerProvided?.escrow?.amount && (
          <HStack
            sx={{
              justifyContent: 'space-between',
              wrap: 'wrap',
              w: '100%',
            }}
          >
            <Typography fontWeight="medium">Escrow Amount</Typography>
            {loading ? (
              <Skeleton w="10rem" h="1rem" />
            ) : (
              <Typography fontWeight="medium">
                {formatCurrencyAmount(
                  loan.customerProvided.escrow.amount,
                  Bank.Currency.USD,
                )}
              </Typography>
            )}
          </HStack>
        )}

        {/* *** Servicer Name *** */}
        {loan?.customerProvided?.servicer?.name && (
          <HStack
            sx={{
              justifyContent: 'space-between',
              wrap: 'wrap',
              w: '100%',
            }}
          >
            <Typography fontWeight="medium">Servicer Name</Typography>
            {loading ? (
              <Skeleton w="10rem" h="1rem" />
            ) : (
              <Typography fontWeight="medium">
                {loan.customerProvided.servicer.name}
              </Typography>
            )}
          </HStack>
        )}
      </VStack>
    </VStack>
  );
};
